import React from 'react';
import { CheckCircle2, AlertTriangle, TrendingUp } from 'lucide-react';
import { MOCK_RESULTS } from './MockExamScreen';

interface Props {
  results: typeof MOCK_RESULTS;
  band: string;
  gapToTarget: number;
}

const BAND_TABLE = [
  { id: 'bt-90', raw: '39–40', band: '9.0', min: 39 },
  { id: 'bt-85', raw: '37–38', band: '8.5', min: 37 },
  { id: 'bt-80', raw: '35–36', band: '8.0', min: 35, target: true },
  { id: 'bt-75', raw: '32–34', band: '7.5', min: 32 },
  { id: 'bt-70', raw: '29–31', band: '7.0', min: 29 },
  { id: 'bt-65', raw: '26–28', band: '6.5', min: 26 },
  { id: 'bt-60', raw: '23–25', band: '6.0', min: 23 },
  { id: 'bt-55', raw: '19–22', band: '5.5', min: 19 },
];

export default function ReportOverview({ results, band, gapToTarget }: Props) {
  return (
    <div className="space-y-6 animate-slide-up">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Passage breakdown */}
        <div className="xl:col-span-2 card-base p-5">
          <h2 className="section-header mb-4">Score by Passage</h2>
          <div className="space-y-4">
            {[
              { id: 'pb-p1', label: 'Passage 1', topic: 'Urban Beekeeping', correct: results.passage1Score, total: results.passage1Total, time: 17 },
              { id: 'pb-p2', label: 'Passage 2', topic: 'Remote Work', correct: results.passage2Score, total: results.passage2Total, time: 22 },
              { id: 'pb-p3', label: 'Passage 3', topic: 'Deep-Sea Mining', correct: results.passage3Score, total: results.passage3Total, time: 22 },
            ].map((p) => {
              const pct = Math.round((p.correct / p.total) * 100);
              return (
                <div key={p.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <span className="text-sm font-semibold text-foreground">{p.label}</span>
                      <span className="text-xs text-muted-foreground ml-2">{p.topic}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground">{p.time} min used</span>
                      <span className={`font-bold tabular-nums ${pct >= 80 ? 'text-accent' : pct >= 65 ? 'text-info' : 'text-warning'}`}>
                        {p.correct}/{p.total}
                      </span>
                    </div>
                  </div>
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${pct >= 80 ? 'bg-accent' : pct >= 65 ? 'bg-info' : 'bg-warning'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{pct}% accuracy</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Band conversion table */}
        <div className="card-base p-5">
          <h2 className="section-header mb-4">Band Conversion Table</h2>
          <div className="space-y-1.5">
            {BAND_TABLE.map((row) => {
              const isCurrent = results.rawScore >= row.min && results.rawScore < (row.min + 3);
              const isTarget = row.target;
              return (
                <div
                  key={row.id}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs ${
                    isCurrent
                      ? 'bg-primary text-primary-foreground font-bold'
                      : isTarget
                      ? 'bg-success-bg border border-accent/40 font-semibold' :'bg-muted'
                  }`}
                >
                  <span className={isCurrent ? 'text-primary-foreground' : isTarget ? 'text-accent' : 'text-muted-foreground'}>
                    {row.raw} correct
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold tabular-nums ${isCurrent ? 'text-primary-foreground' : isTarget ? 'text-accent' : 'text-foreground'}`}>
                      Band {row.band}
                    </span>
                    {isCurrent && <span className="text-xs text-primary-foreground/80">← You</span>}
                    {isTarget && !isCurrent && <span className="text-xs text-accent">← Target</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {gapToTarget > 0 && (
            <div className="mt-3 p-2.5 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-xs text-primary font-semibold">
                {gapToTarget} more correct answer{gapToTarget !== 1 ? 's' : ''} → Band 8.0
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Key findings */}
      <div className="card-base p-5">
        <h2 className="section-header mb-4">Key Findings</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              id: 'kf-strength',
              icon: <CheckCircle2 size={16} className="text-accent" />,
              title: 'Strengths',
              bg: 'bg-success-bg border-accent/30',
              items: [
                'Short-Answer Questions: 100% accuracy (2/2)',
                'Multiple Choice: 80% accuracy (8/10) — within target',
                'Passage 1 completed in 17 min — 3 min under allocation',
              ],
            },
            {
              id: 'kf-risk',
              icon: <AlertTriangle size={16} className="text-warning" />,
              title: 'At Risk',
              bg: 'bg-warning-bg border-warning/30',
              items: [
                'Matching Headings: 50% accuracy (3/6) — biggest band-loss type',
                'YN/NG: 60% accuracy (3/5) — distractor confusion rate 38%',
                'Passage 3 overran by 2 min — pacing pressure on final questions',
              ],
            },
            {
              id: 'kf-action',
              icon: <TrendingUp size={16} className="text-primary" />,
              title: 'Band-Flip Opportunities',
              bg: 'bg-secondary border-primary/30',
              items: [
                '1 more Matching Headings correct → +1 raw mark',
                '1 more YN/NG correct → +1 raw mark',
                '1 more TF/NG correct → +1 raw mark',
                '3 corrections total → Band 8.0 achieved',
              ],
            },
          ].map((finding) => (
            <div key={finding.id} className={`p-4 rounded-xl border ${finding.bg}`}>
              <div className="flex items-center gap-2 mb-3">
                {finding.icon}
                <p className="text-sm font-semibold text-foreground">{finding.title}</p>
              </div>
              <ul className="space-y-1.5">
                {finding.items.map((item, i) => (
                  <li key={`${finding.id}-item-${i}`} className="text-xs text-foreground leading-relaxed flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-muted-foreground flex-shrink-0 mt-1.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}